import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  FilterType,
  Message,
  ScheduleMessages,
  Thread,
  ThreadStatus,
  User,
  ViewType,
} from "../../../modules/EHR/Patients/Charting/Messaging/src/utils/interfaces";
import { isSameMessageContent } from "../../../modules/EHR/Patients/Charting/Messaging/src/utils/pendingMessages";

export interface PendingThreadTarget {
  uuid: string | null;
  /** The subject submitted, used to identify the thread when no uuid came back. */
  subject: string;
  knownThreadIds: string[];
  /** Epoch ms, so a target that never resolves cannot fire much later. */
  requestedAt: number;
}

interface BookmarkedMessageDetails {
  messageId: string;
  threadId: string;
  positionInThread: number;
}

export interface OutboundMessagePayload {
  threadUuid: string;
  senderUuid: string;
  senderName: string;
  content: string;
  scheduled: boolean;
  urgent: boolean;
  scheduledTime: string | null;
  attachments:
    | { fileKey: string; fileName: string; fileType: string; key?: string }[]
    | null;
  type: "TEXT" | "IMAGE" | "FILE" | "SYSTEM";
}

interface CancelledScheduledDraft {
  content: string;
  urgent: boolean;
  attachments?: {
    fileKey?: string;
    fileName?: string;
    fileType?: string;
  }[];
}

interface MessagingState {
  threads: Thread[];
  currentThread: Thread | null;
  isSidebarOpen: boolean;
  filterType: FilterType;
  scheduledMessages: ScheduleMessages[];
  viewType: ViewType;
  bookmarkedThreads: Thread[];
  bookmarkedMessages: Message[];
  selectedBookmarkedMessage: string | null;
  bookmarkedMessagesThreadId: string | null;
  bookmarkedMessageDetails: BookmarkedMessageDetails | null;
  currentUser: User | null;
  cancelledScheduledDrafts: Record<string, CancelledScheduledDraft | undefined>;
  // Unsettled outbound payloads, keyed by the optimistic message's clientId.
  outbox: Record<string, OutboundMessagePayload | undefined>;
  // A thread that has just been created and should be opened as soon as it
  // shows up in the list. The create response is neither the populated list
  // shape nor reliably carrying the new uuid, so the ids present at creation
  // time are recorded too -- whatever id is in the refreshed list but not in
  // that set is the thread that was just made.
  pendingThread: PendingThreadTarget | null;
}

const initialState: MessagingState = {
  threads: [],
  currentThread: null,
  isSidebarOpen: true,
  filterType: "OPEN",
  scheduledMessages: [],
  viewType: "normal",
  bookmarkedThreads: [],
  bookmarkedMessages: [],
  selectedBookmarkedMessage: null,
  bookmarkedMessagesThreadId: null,
  bookmarkedMessageDetails: null,
  currentUser: null,
  cancelledScheduledDrafts: {},
  outbox: {},
  pendingThread: null,
};

export const messagingSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setCurrentUser: (state, action: PayloadAction<User>) => {
      state.currentUser = action.payload;
    },

    setThreads: (state, action: PayloadAction<Thread[]>) => {
      state.threads = action.payload;
    },

    setCurrentThread: (state, action: PayloadAction<Thread | null>) => {
      state.currentThread = action.payload;
    },

    setPendingThread: (
      state,
      action: PayloadAction<PendingThreadTarget | null>,
    ) => {
      state.pendingThread = action.payload;
    },

    toggleSidebar: (state) => {
      state.isSidebarOpen = !state.isSidebarOpen;
    },

    setFilterType: (state, action: PayloadAction<FilterType>) => {
      state.filterType = action.payload;
    },

    setViewType: (state, action: PayloadAction<ViewType>) => {
      state.viewType = action.payload;
    },

    setBookmarkedMessagesThreadId: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.bookmarkedMessagesThreadId = action.payload;
    },

    handleSelectedBookmarkMessage: (state, action: PayloadAction<string>) => {
      state.selectedBookmarkedMessage = action.payload;
    },

    setBookmarkedMessageDetails: (
      state,
      action: PayloadAction<BookmarkedMessageDetails | null>,
    ) => {
      state.bookmarkedMessageDetails = action.payload;
    },

    addThread: (state, action: PayloadAction<Thread>) => {
      state.threads.push(action.payload);
    },

    addMessage: (
      state,
      action: PayloadAction<{
        threadId: string;
        message: Omit<Message, "id" | "timestamp">;
        options?: { id?: string; timestamp?: Date };
      }>,
    ) => {
      const { threadId, message, options } = action.payload;
      const thread = state.threads.find((t) => t.id === threadId);
      if (!thread) return;

      const newId = options?.id || `msg_${Date.now()}`;

      if (thread.messages.some((m) => m.id === newId)) return;

      const newMessage: Message = {
        ...message,
        id: newId,
        timestamp: new Date(),
        isSentOnBehalf: message.isSentOnBehalf, // Explicitly preserve isSentOnBehalf
      };

      thread.messages.push(newMessage);
      thread.lastMessage = newMessage;

      // Also update currentThread if it's the same thread
      if (state.currentThread && state.currentThread.id === threadId) {
        if (!state.currentThread.messages.some((m) => m.id === newId)) {
          state.currentThread.messages.push(newMessage);
          state.currentThread.lastMessage = newMessage;
        }
      }
    },

    // Swaps the optimistic copy for the stored message **in place** once the
    // server echoes it back. Replacing rather than remove-then-append keeps the
    // bubble where it already is, so the spinner turns into a timestamp
    // instead of the message disappearing and re-entering at the bottom.
    // Matched on sender + content because the socket payload carries no
    // client-side correlation id.
    settlePendingMessage: (
      state,
      action: PayloadAction<{
        threadId: string;
        senderId: string;
        content: string;
        id: string;
        message: Omit<Message, "id" | "timestamp">;
      }>,
    ) => {
      const { threadId, senderId, content, id, message } = action.payload;

      const swapPending = (messages: Message[]) => {
        let index = messages.findIndex(
          (m) =>
            m.isPending &&
            m.sender?.id === senderId &&
            isSameMessageContent(m.content, content),
        );

        if (index === -1) {
          index = messages.findIndex(
            (m) => m.isPending && m.sender?.id === senderId,
          );
        }
        if (index === -1) return;

        const settledClientId = messages[index].clientId;
        if (settledClientId) delete state.outbox[settledClientId];

        // Already settled under the real id (both thread copies share it).
        if (messages.some((m) => m.id === id)) {
          messages.splice(index, 1);
          return;
        }

        messages[index] = {
          ...message,
          id,
          // Same React key as the pending bubble, so this updates in place
          // rather than unmounting and remounting as a new list item.
          clientId: messages[index].clientId,
          // Keep the timestamp already on screen so the time does not jump.
          timestamp: messages[index].timestamp,
          isPending: false,
          sendFailed: false,
        };
      };

      const thread = state.threads.find((t) => t.id === threadId);
      if (thread) swapPending(thread.messages);
      if (state.currentThread?.id === threadId) {
        swapPending(state.currentThread.messages);
      }
    },

    markMessageSendFailed: (
      state,
      action: PayloadAction<{ threadId: string; messageId: string }>,
    ) => {
      const { threadId, messageId } = action.payload;

      const flagFailed = (messages: Message[]) => {
        const message = messages.find((m) => m.id === messageId && m.isPending);
        if (message) {
          message.isPending = false;
          message.sendFailed = true;
        }
      };

      const thread = state.threads.find((t) => t.id === threadId);
      if (thread) flagFailed(thread.messages);
      if (state.currentThread?.id === threadId) {
        flagFailed(state.currentThread.messages);
      }
    },

    // Holds the wire payload for a message that has just been sent
    // optimistically, so Retry can re-emit it unchanged.
    queueOutboundMessage: (
      state,
      action: PayloadAction<{
        clientId: string;
        payload: OutboundMessagePayload;
      }>,
    ) => {
      state.outbox[action.payload.clientId] = action.payload.payload;
    },

    // Puts a failed message back into the sending state, so the bubble shows
    // the spinner again while the retry is in flight.
    retryMessageSend: (
      state,
      action: PayloadAction<{ threadId: string; messageId: string }>,
    ) => {
      const { threadId, messageId } = action.payload;
      const flagPending = (messages: Message[]) => {
        const message = messages.find(
          (m) => m.id === messageId && m.sendFailed,
        );
        if (message) {
          message.sendFailed = false;
          message.isPending = true;
        }
      };

      const thread = state.threads.find((t) => t.id === threadId);
      if (thread) flagPending(thread.messages);
      if (state.currentThread?.id === threadId) {
        flagPending(state.currentThread.messages);
      }
    },

    // Drops a message that was never sent, so a permanently failed one can be
    // cleared instead of sitting in the thread forever.
    discardFailedMessage: (
      state,
      action: PayloadAction<{ threadId: string; messageId: string }>,
    ) => {
      const { threadId, messageId } = action.payload;

      const removeFailed = (messages: Message[]) => {
        const index = messages.findIndex(
          (m) => m.id === messageId && m.sendFailed,
        );
        if (index !== -1) messages.splice(index, 1);
      };

      const thread = state.threads.find((t) => t.id === threadId);
      if (thread) {
        removeFailed(thread.messages);
        thread.lastMessage = thread.messages[thread.messages.length - 1];
      }
      if (state.currentThread?.id === threadId) {
        removeFailed(state.currentThread.messages);
        state.currentThread.lastMessage =
          state.currentThread.messages[state.currentThread.messages.length - 1];
      }

      delete state.outbox[messageId];
    },

    markThreadAsRead: (state, action: PayloadAction<string>) => {
      const thread = state.threads.find((t) => t.id === action.payload);
      if (!thread) return;

      thread.status = "read";
      thread.unreadCount = 0;
      thread.messages = thread.messages.map((msg) => ({
        ...msg,
        isRead: true,
      }));
    },

    // Patch a single thread's real-time fields (status / unread / last message)
    // from a `thread_list_updated` socket event, so the thread's status stays
    // in sync wherever the messaging redux state is consumed.
    updateThreadRealtimeStatus: (
      state,
      action: PayloadAction<{
        threadId: string;
        status?: ThreadStatus;
        unreadCount?: number;
        isResolved?: boolean;
        lastMessage?: string;
        lastMessageTime?: string;
        urgentMessageCount?: number;
      }>,
    ) => {
      const {
        threadId,
        status,
        unreadCount,
        isResolved,
        lastMessage,
        lastMessageTime,
        urgentMessageCount,
      } = action.payload;

      const patch = (thread?: Thread | null) => {
        if (!thread || thread.id !== threadId) return;
        if (unreadCount !== undefined) thread.unreadCount = unreadCount;
        if (urgentMessageCount !== undefined) {
          thread.urgentMessageCount = urgentMessageCount;
        }
        if (lastMessage !== undefined && thread.lastMessage) {
          thread.lastMessage.content = lastMessage;
        }
        if (lastMessageTime !== undefined) {
          thread.updated = new Date(lastMessageTime);
        }
        if (isResolved !== undefined) thread.isResolved = isResolved;

        // Reconcile the single `status` field. A resolved thread always shows
        // "resolved"; otherwise fall back to the read/unread status. Never
        // downgrade a still-resolved thread to read/unread.
        const resolvedNow =
          isResolved !== undefined ? isResolved : thread.isResolved;
        if (resolvedNow) {
          thread.status = "resolved";
        } else if (status !== undefined) {
          thread.status = status;
        }
      };

      patch(state.threads.find((t) => t.id === threadId));
      if (state.currentThread?.id === threadId) patch(state.currentThread);
    },

    /**
     * Sets a thread's bookmark everywhere it is held: the list copy, the open
     * thread and the bookmarked list. Taking the value rather than flipping it
     * keeps the copies from drifting apart when one of them was already stale.
     */
    setThreadBookmark: (
      state,
      action: PayloadAction<{ threadId: string; isBookmarked: boolean }>,
    ) => {
      const { threadId, isBookmarked } = action.payload;
      const thread = state.threads.find((t) => t.id === threadId);
      if (thread) thread.isBookmarked = isBookmarked;
      if (state.currentThread?.id === threadId) {
        state.currentThread.isBookmarked = isBookmarked;
      }

      state.bookmarkedThreads = state.bookmarkedThreads.filter(
        (t) => t.id !== threadId,
      );
      const bookmarked = thread ?? state.currentThread;
      if (isBookmarked && bookmarked?.id === threadId) {
        state.bookmarkedThreads.push(bookmarked);
      }
    },
    toggleThreadStar: (state, action: PayloadAction<string>) => {
      const thread = state.threads.find((t) => t.id === action.payload);
      if (!thread) return;

      thread.isBookmarked = !thread.isBookmarked;

      if (thread.isBookmarked) {
        state.bookmarkedThreads.push(thread);
      } else {
        state.bookmarkedThreads = state.bookmarkedThreads.filter(
          (t) => t.id !== thread.id,
        );
      }
    },

    toggleMessageStar: (state, action: PayloadAction<string>) => {
      state.threads.forEach((thread) => {
        thread.messages.forEach((msg) => {
          if (msg.id === action.payload) {
            msg.isBookmarked = !msg.isBookmarked;

            if (msg.isBookmarked) {
              state.bookmarkedMessages.push(msg);
            } else {
              state.bookmarkedMessages = state.bookmarkedMessages.filter(
                (m) => m.id !== msg.id,
              );
            }
          }
        });
      });

      // Also update currentThread if it contains this message
      if (state.currentThread) {
        state.currentThread.messages.forEach((msg) => {
          if (msg.id === action.payload) {
            msg.isBookmarked = !msg.isBookmarked;
          }
        });
      }
    },

    unbookmarkAllMessagesInThread: (state, action: PayloadAction<string>) => {
      const threadId = action.payload;
      const thread = state.threads.find((t) => t.id === threadId);

      if (thread) {
        // Get all message IDs that are bookmarked in this thread
        const bookmarkedMessageIds = thread.messages
          .filter((msg) => msg.isBookmarked)
          .map((msg) => msg.id);

        // Unbookmark all messages in the thread
        thread.messages.forEach((msg) => {
          msg.isBookmarked = false;
        });

        // Remove these messages from bookmarkedMessages array
        state.bookmarkedMessages = state.bookmarkedMessages.filter(
          (m) => !bookmarkedMessageIds.includes(m.id),
        );
      }

      // Also update currentThread if it's the same thread
      if (state.currentThread && state.currentThread.id === threadId) {
        state.currentThread.messages.forEach((msg) => {
          msg.isBookmarked = false;
        });
      }
    },

    toggleMessageUrgent: (state, action: PayloadAction<string>) => {
      let updatedThreadId: string | null = null;
      let updatedUrgentValue: boolean | null = null;

      state.threads.forEach((thread) => {
        const msg = thread.messages.find((m) => m.id === action.payload);
        if (!msg) return;

        msg.isUrgent = !msg.isUrgent;
        updatedThreadId = thread.id;
        updatedUrgentValue = msg.isUrgent;

        thread.urgentMessageCount = msg.isUrgent
          ? (thread.urgentMessageCount || 0) + 1
          : Math.max((thread.urgentMessageCount || 0) - 1, 0);
      });

      if (
        state.currentThread &&
        updatedThreadId &&
        state.currentThread.id === updatedThreadId &&
        updatedUrgentValue !== null
      ) {
        const currentMsg = state.currentThread.messages.find(
          (m) => m.id === action.payload,
        );

        if (currentMsg) {
          currentMsg.isUrgent = updatedUrgentValue;
          state.currentThread.urgentMessageCount =
            state.currentThread.messages.filter((m) => m.isUrgent).length;
        }
      }
    },

    resolveThread: (state, action: PayloadAction<string>) => {
      const thread = state.threads.find((t) => t.id === action.payload);
      if (thread) {
        thread.isResolved = true;
        thread.status = "resolved";
      }
    },

    resumeThread: (state, action: PayloadAction<string>) => {
      const thread = state.threads.find((t) => t.id === action.payload);
      if (thread) {
        thread.isResolved = false;
        thread.status = "read";
      }
    },

    handleScheduleMessages: (
      state,
      action: PayloadAction<ScheduleMessages>,
    ) => {
      state.scheduledMessages.push(action.payload);
    },

    setCancelledScheduledDraft: (
      state,
      action: PayloadAction<{
        threadId: string;
        draft: CancelledScheduledDraft;
      }>,
    ) => {
      state.cancelledScheduledDrafts[action.payload.threadId] =
        action.payload.draft;
    },

    clearCancelledScheduledDraft: (state, action: PayloadAction<string>) => {
      delete state.cancelledScheduledDrafts[action.payload];
    },

    resetMessagingState: (state) => {
      return initialState;
    },
  },
});

export const {
  setCurrentUser,
  setThreads,
  setCurrentThread,
  setPendingThread,
  toggleSidebar,
  setFilterType,
  setViewType,
  setBookmarkedMessagesThreadId,
  handleSelectedBookmarkMessage,
  setBookmarkedMessageDetails,
  addThread,
  addMessage,
  settlePendingMessage,
  markMessageSendFailed,
  queueOutboundMessage,
  retryMessageSend,
  discardFailedMessage,
  markThreadAsRead,
  updateThreadRealtimeStatus,
  toggleThreadStar,
  setThreadBookmark,
  toggleMessageStar,
  unbookmarkAllMessagesInThread,
  toggleMessageUrgent,
  resolveThread,
  resumeThread,
  handleScheduleMessages,
  setCancelledScheduledDraft,
  clearCancelledScheduledDraft,
  resetMessagingState,
} = messagingSlice.actions;

export default messagingSlice.reducer;
